import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { reviewService } from '../../services/reviewService';

interface RatingDimension {
  key: string;
  label: string;
  value: number;
}

const CLIENT_DIMENSIONS = [
  { key: 'outputQuality', label: '作品质量' },
  { key: 'communication', label: '沟通效率' },
  { key: 'timeliness', label: '交付准时' },
  { key: 'professionalism', label: '专业程度' },
];

const CREATOR_DIMENSIONS = [
  { key: 'communication', label: '沟通效率' },
  { key: 'requirementClarity', label: '需求清晰' },
  { key: 'paymentTimeliness', label: '付款及时' },
  { key: 'cooperationAttitude', label: '合作态度' },
];

const CLIENT_GOOD_TAGS = ['出片质量高', '沟通顺畅', '交付准时', '超出预期', '修改耐心', '创意出色'];
const CLIENT_BAD_TAGS = ['交付延迟', '沟通不及时', '与预期不符', '需多次返工'];
const CREATOR_GOOD_TAGS = ['需求明确', '反馈及时', '付款爽快', '尊重创作', '沟通友好'];
const CREATOR_BAD_TAGS = ['需求模糊', '反复修改', '付款拖延', '沟通困难'];

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
  revieweeName: string;
  role: 'client_to_creator' | 'creator_to_client';
  onSubmitted?: () => void;
}

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5 transition-transform hover:scale-110"
        >
          <Star className={`w-5 h-5 ${star <= (hover || value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
};

export const ReviewModal: React.FC<ReviewModalProps> = ({ isOpen, onClose, contractId, revieweeName, role, onSubmitted }) => {
  const dimensions = role === 'client_to_creator' ? CLIENT_DIMENSIONS : CREATOR_DIMENSIONS;
  const goodTags = role === 'client_to_creator' ? CLIENT_GOOD_TAGS : CREATOR_GOOD_TAGS;
  const badTags = role === 'client_to_creator' ? CLIENT_BAD_TAGS : CREATOR_BAD_TAGS;

  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const overallRating = dimensions.length > 0
    ? Math.round((Object.values(ratings).reduce((a, b) => a + b, 0) / dimensions.length) * 10) / 10
    : 0;

  const handleTagClick = (tag: string) => {
    const newTags = new Set(selectedTags);
    if (newTags.has(tag)) {
      newTags.delete(tag);
      // Remove from comment
      setComment(prev => prev.replace(tag, '').replace(/，\s*，/g, '，').replace(/^，\s*|，\s*$/g, '').trim());
    } else {
      newTags.add(tag);
      // Append to comment with dedup
      setComment(prev => {
        if (prev.includes(tag)) return prev;
        return prev ? `${prev}，${tag}` : tag;
      });
    }
    setSelectedTags(newTags);
  };

  const handleSubmit = async () => {
    const unrated = dimensions.filter(d => !ratings[d.key]);
    if (unrated.length > 0) {
      setError(`请完成所有评分（${unrated.map(d => d.label).join('、')}）`);
      return;
    }
    if (!comment.trim()) { setError('请填写评价内容'); return; }

    setSubmitting(true);
    setError('');
    try {
      const res = await reviewService.submitReview(contractId, Math.round(overallRating), comment.trim(), ratings);
      if (res.success) {
        onSubmitted?.();
        onClose();
      } else {
        setError(res.error || '提交失败');
      }
    } catch {
      setError('提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`评价 ${revieweeName}`} size="md">
      <div className="space-y-5">
        {/* Dimension Ratings */}
        <div className="space-y-3">
          {dimensions.map((dim) => (
            <div key={dim.key} className="flex items-center justify-between">
              <span className="text-sm text-gray-700 font-medium w-20">{dim.label}</span>
              <StarRating value={ratings[dim.key] || 0} onChange={(v) => setRatings(prev => ({ ...prev, [dim.key]: v }))} />
            </div>
          ))}
          {overallRating > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className="text-sm font-semibold text-gray-900">综合评分</span>
              <span className="text-lg font-bold text-yellow-500">{overallRating}</span>
            </div>
          )}
        </div>

        {/* Quick Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">快捷印象</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {goodTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagClick(tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${selectedTags.has(tag) ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-200 text-gray-600 hover:border-green-300'}`}
              >
                👍 {tag}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {badTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagClick(tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${selectedTags.has(tag) ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-200 text-gray-600 hover:border-red-300'}`}
              >
                👎 {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">详细评价</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="分享你的合作体验..."
            rows={4}
            maxLength={500}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
          <div className="text-right text-xs text-gray-400 mt-1">{comment.length}/500</div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting ? '提交中...' : '提交评价'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
